/**
 * /learn "Touch markets" interactive example (#786): pick a made-up price path and a target; the chart shows the price,
 * its short trailing average (the TWAP the rule uses), the target line and where the market settles. The maths is
 * learn/touch.ts (shared with the native Learn screens via shared/fixtures/learn.json).
 */
import { useId, useState } from 'react';
import { TOUCH_EXAMPLE_TWAP, TOUCH_PATHS, touchPathResult, type TouchPathKey } from './touch';
import { whenLabel } from '../describe/when';
import { usd } from '../i18n/format';
import type { PlainKey } from '../i18n/i18n';
import Msg from '../i18n/Msg';
import { useT } from '../i18n/useT';

const W = 400;
const H = 170;
const PAD = { l: 34, r: 14, t: 10, b: 22 };
const Y_MIN = 98;
const Y_MAX = 120;

const PATH_LABEL: Record<TouchPathKey, PlainKey> = { early: 'learn.touch_ex.path_early', never: 'learn.touch_ex.path_never' };

const x = (i: number, n: number) => PAD.l + (i / (n - 1)) * (W - PAD.l - PAD.r);
const y = (v: number) => PAD.t + (1 - (v - Y_MIN) / (Y_MAX - Y_MIN)) * (H - PAD.t - PAD.b);
const pts = (vals: readonly number[], from = 0) => vals.map((v, i) => `${x(i + from, TOUCH_PATHS.early.length).toFixed(1)},${y(v).toFixed(1)}`).join(' ');

function trailingAvg(path: readonly number[], w: number): number[] {
  return path.map((_, i) => {
    const from = Math.max(0, i - w + 1);
    const s = path.slice(from, i + 1);
    return s.reduce((a, b) => a + b, 0) / s.length;
  });
}

const money = (v: number) => usd(v % 1 === 0 ? v.toFixed(0) : v.toFixed(1));

export default function TouchExample() {
  const [key, setKey] = useState<TouchPathKey>('early');
  const [target, setTarget] = useState(110);
  const sliderId = useId();
  const t = useT();
  const path = TOUCH_PATHS[key];
  const n = path.length;
  const r = touchPathResult(path, target, TOUCH_EXAMPLE_TWAP);
  const avg = trailingAvg(path, TOUCH_EXAMPLE_TWAP);
  const cut = r.touchedAt ?? n - 1;

  return (
    <div className="space-y-3" data-testid="learn-touch">
      <div role="radiogroup" aria-label={t('learn.touch_ex.path_group')} className="flex max-w-md rounded-lg bg-surface-2/60 p-1">
        {(Object.keys(TOUCH_PATHS) as TouchPathKey[]).map((k) => (
          <button
            key={k}
            type="button"
            role="radio"
            aria-checked={key === k}
            onClick={() => setKey(k)}
            className={`flex-1 rounded-md px-2 py-1.5 text-xs font-medium ${key === k ? 'bg-surface text-fg' : 'text-muted hover:text-fg'}`}
          >
            {t(PATH_LABEL[k])}
          </button>
        ))}
      </div>
      <div>
        <label htmlFor={sliderId} className="flex items-center justify-between text-sm font-medium">
          <span>{t('touch.target_price')}</span>
          <span className="num text-warn" data-testid="learn-touch-target">
            {money(target)}
          </span>
        </label>
        <input
          id={sliderId}
          type="range"
          min={102}
          max={118}
          step={0.5}
          value={target}
          onChange={(e) => setTarget(Number(e.target.value))}
          aria-valuetext={t('learn.touch_ex.target_value', { price: money(target) })}
          className="w-full accent-[rgb(var(--warn))]"
        />
      </div>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="h-auto w-full rounded-md bg-surface-2/40"
        role="img"
        aria-label={t('learn.touch_ex.chart', { path: t(PATH_LABEL[key]), price: money(target) })}
        data-testid="learn-touch-chart"
      >
        {[100, 110, 120].map((v) => (
          <g key={v}>
            <line x1={PAD.l} x2={W - PAD.r} y1={y(v)} y2={y(v)} className="stroke-border" strokeWidth={0.5} />
            <text x={PAD.l - 4} y={y(v) + 3} textAnchor="end" className="fill-muted text-[9px]">
              {v}
            </text>
          </g>
        ))}
        <text x={PAD.l} y={H - 6} className="fill-muted text-[9px]">
          {t('learn.touch_ex.listed')}
        </text>
        <line x1={x(n - 1, n)} x2={x(n - 1, n)} y1={PAD.t} y2={H - PAD.b} className="stroke-muted" strokeDasharray="2 2" />
        <text x={x(n - 1, n)} y={H - 6} textAnchor="end" className="fill-muted text-[9px]">
          {whenLabel('Deadline')}
        </text>
        <line
          x1={PAD.l}
          x2={W - PAD.r}
          y1={y(target)}
          y2={y(target)}
          className="stroke-warn"
          strokeDasharray="5 3"
          strokeWidth={1.2}
          data-testid="learn-touch-target-line"
        />
        {/* After a touch the market is over: the rest of the path is drawn faded. */}
        {cut < n - 1 && (
          <polyline points={pts(path.slice(cut), cut)} fill="none" className="stroke-muted opacity-50" strokeWidth={1} />
        )}
        <polyline points={pts(path.slice(0, cut + 1))} fill="none" className="stroke-accent" strokeWidth={1.4} />
        <polyline points={pts(avg.slice(0, cut + 1))} fill="none" className="stroke-fg opacity-60" strokeWidth={1} strokeDasharray="3 2" />
        {r.touchedAt !== null && (
          <g data-testid="learn-touch-marker">
            <line x1={x(cut, n)} x2={x(cut, n)} y1={PAD.t} y2={H - PAD.b} className="stroke-yes" strokeWidth={1} />
            <circle cx={x(cut, n)} cy={y(avg[cut]!)} r={3.5} className="fill-yes" />
          </g>
        )}
      </svg>
      <p className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-muted" aria-hidden="true">
        <span>
          <span className="text-accent">━</span> {t('learn.touch_ex.legend_price')}
        </span>
        <span>
          <span className="text-fg/60">┅</span> {t('learn.touch_ex.legend_avg', { n: TOUCH_EXAMPLE_TWAP })}
        </span>
        <span>
          <span className="text-warn">┅</span> {t('learn.touch_ex.legend_target')}
        </span>
      </p>
      <p
        className={`rounded-md p-3 text-sm ${r.result === 'yes' ? 'bg-yes/10' : 'bg-no/10'}`}
        aria-live="polite"
        data-testid="learn-touch-result"
      >
        {r.touchedAt !== null ? (
          r.early ? (
            <Msg
              k="learn.touch_ex.touched_early"
              args={{
                head: <strong className="text-yes">{t('learn.touch_ex.touched_head', { n: r.touchedAt })}</strong>,
                price: money(target),
                yes: <strong>{t('common.yes')}</strong>,
                n: r.stepsEarly,
              }}
            />
          ) : (
            <Msg
              k="learn.touch_ex.touched_deadline"
              args={{
                head: <strong className="text-yes">{t('learn.touch_ex.touched_head', { n: r.touchedAt })}</strong>,
                price: money(target),
                yes: <strong>{t('common.yes')}</strong>,
              }}
            />
          )
        ) : (
          <Msg
            k="learn.touch_ex.never"
            args={{
              head: <strong className="text-no">{t('learn.touch_ex.never_head')}</strong>,
              price: money(target),
              no: <strong>{t('common.no')}</strong>,
            }}
          />
        )}
      </p>
    </div>
  );
}
